import { useCallback, useMemo } from "react";
import { useKeywordNameTableCell } from "@vms/TableCells/useKeywordNameTableCell";
import { useMacStageTableCell } from "@vms/TableCells/useMacStageTableCell";
import { useBaseSizeTableCell } from "@vms/TableCells/useBaseSizeTableCell";
import { useGetMetricRawTableCell } from "@vms/TableCells/useGetMetricRawTableCell";
import { HOME_INDEX_METRICS_ORDER } from "@models/Metric";
import { getRem } from "@datassential/platform-ui-lib";
import { TableColumn } from "@components/Table/TableColumns";
import { HomeTableIndexDataItem } from "@api/useHomeTableIndexData";

export type TableViewIndexTableConfigProps = {
    data: HomeTableIndexDataItem[];
    search: string;
}

export function useTableViewIndexTableConfig({
    data,
    search,
}: TableViewIndexTableConfigProps) {
    const trimmedSearch = useMemo(() => search.trim(), [search]);
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch]);

    const nameColumn = useKeywordNameTableCell(data, trimmedSearch);
    const macStageColumn = useMacStageTableCell(data);
    const baseSizeColumn = useBaseSizeTableCell(data);
    const getMetricIndex = useGetMetricRawTableCell(data, HOME_INDEX_METRICS_ORDER);

    const getMetricStyle = useCallback(
        (index: number) => {
            return index === 0 ? {
                borderLeftWidth: getRem(3),
                paddingLeft: getRem(20),
            } : undefined;
        },
        []
    );

    const indexMetrics = useMemo(
        () => HOME_INDEX_METRICS_ORDER.map(
            (_, index) => getMetricIndex(index)
        ),
        [getMetricIndex]
    );

    return useMemo(
        (): TableColumn[] => [
            {
                ...nameColumn,
                sortKey: hideSorting ? undefined : 'name',
            },
            {
                ...macStageColumn,
                sortKey: hideSorting ? undefined : 'macStage',
            },
            {
                ...baseSizeColumn,
                sortKey: hideSorting ? undefined : 'base',
            },
            ...indexMetrics.map((indexConfig, index) => ({
                ...indexConfig,
                sortKey: hideSorting ? undefined : `metric${index}`,
                isHiddenForTextMode: true,
                style: {
                    ...indexConfig.style,
                    cell: {
                        ...indexConfig.style?.cell,
                        ...getMetricStyle(index),
                    },
                },
            })),
            {
                key: 'index-metrics',
                isHiddenForRender: true,
                head: '',
                headText: 'Index',
                Value: () => null,
                subColumns: indexMetrics,
            },
        ],
        [
            nameColumn,
            macStageColumn,
            baseSizeColumn,
            indexMetrics,
            hideSorting,
            getMetricStyle,
        ]
    );
}